<!-- Add bulk options with three dots button -->
<template>
  <div class="container py-5">

    <div class="level">
      <div class="level-left">
        <button 
          class="button is-primary is-small mb-0 mr-2" 
          @click="addingUser = true;">
          <span class="icon">
            <i class="fas fa-plus"/>
          </span>
        </button>
        <div class="title is-4 mr-3 ml-1 my-2">
          Users
        </div>
      </div>

      <div class="level-right flex--align-end">
        <div class="px-2">
          <label class="menu-label">
            Search
          </label>
          <v-search
            class="mt-1"
            :search-input.sync="filters.display_name"
            @search="filterUsers('display_name', $event)"
            placeholder="Search users by name"
            @clear="setFilter({ field: 'display_name', value: '' })"
            />
        </div>

        <div class="px-2">
          <label class="menu-label">
            Status
            <span 
              v-if="filters.is_active" 
              @click="setFilter({ field: 'is_active', value: '' })"
              class="icon is-clickable">
              <i class="fas fa-times"/>
            </span>
          </label>
          <v-select
            class="mt-1"
            :options="statusOptions"
            item-text="name"
            item-value="value"
            v-model="filters.is_active"
            @input="filterUsers('is_active', $event)"
          />
        </div>

        <div v-if="roles" class="px-2">
          <label class="menu-label">
            Role
            <span 
              v-if="filters.role" 
              @click="setFilter({ field: 'role', value: '' })"
              class="icon is-clickable">
              <i class="fas fa-times"/>
            </span>
          </label>
          <v-select
            class="mt-1"
            :options="roles"
            v-model="filters.role"
            @input="filterUsers('role', $event)"
          />
        </div>

        <div v-if="policies" class="px-2">
          <label class="menu-label">
            Policy
            <span 
              v-if="filters.policy" 
              @click="setFilter({ field: 'policy', value: '' })"
              class="icon is-clickable">
              <i class="fas fa-times"/>
            </span>
          </label>
          <div class="field has-addons">
            <div class="control">
              <v-select
                class="mt-1"
                :options="policies"
                item-text="name"
                item-value="id"
                v-model="filters.policy"
                @input="filterUsers('policy', $event)"
              />
            </div>
            <div class="control">
              <v-tooltip message="Edit Policies">
                <router-link
                  :to="{ name: 'Permissions' }" 
                  class="button mt-1 is-info">
                  <span class="icon">
                    <i class="far fa-edit"></i>
                  </span>
                </router-link>
              </v-tooltip>
            </div>
          </div>
        </div>

        <v-fade-transition mode="out-in">
          <button 
            v-if="selected.length > 0"
            @click="bulkUpdating = true"
            class="button is-primary">
            <span>
              Bulk Update {{ selected.length }} Users
            </span>
          </button>
        </v-fade-transition>
      </div>
    </div>    

    <v-fade-transition mode="out-in">
      <v-alert 
        class="my-3"
        v-if="!loading && filteredUserList.length == 0"
        type="info">
        There are no users.
      </v-alert>

      <table v-else class="table is-fullwidth is-hoverable is-clickable">
        <thead>
          <tr>
            <th>
              <v-tooltip :message="'Select all / none'">
                <v-checkbox 
                  :value="allSelected" 
                  @input="toggleAllSelected($event)"/>
              </v-tooltip>
            </th>
            <th 
              v-for="(header, headerIndex) in headers()"
              @click="header.sortable ? 
                setOrderBy({ 
                  field: header.field, 
                  direction: orderBy.current.direction == 'asc' ? 'desc' : 'asc' 
                }) : ''"
              :key="headerIndex"
              :class="{ 'is-sortable': header.sortable }">
              {{ header.label }}
              <span 
                v-if="header.sortable && header.field == orderBy.current.field" 
                class="icon is-small is-right ml-1 rotatable"
                :class="{ 'desc': orderBy.current.direction == 'desc' && 
                  header.field == orderBy.current.field }">
                <i class="fas fa-chevron-up order-by-button"></i>
              </span>
            </th>
          </tr>
          <tr v-if="loading">
            <th :colspan="headers().length + 1" class="py-0 px-0 is-naked">
              <v-progress class="is-fullwidth" v-model="loading"/>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr 
            class="v-centered"
            v-for="user in filteredUserList"
            :key="user.id">
            <td :key="'check' + user.id">
              <v-checkbox 
                @click.native.stop=""
                :value="userIsSelected(user.id)"
                @input="toggleSelected(user.id)"/>
            </td>            
            <template v-for="(header, headerIndex) in headers()">
              <td v-if="header.type == 'select'"
                :key="headerIndex">
                <v-select
                  :disabled="!user.is_active"
                  v-if="policies.length > 0"
                  :options="policies"
                  item-text="name"
                  item-value="id"
                  @input="selectUserPolicy(user, $event)"
                  :value="selectValue(user, header, true)"
                />  
              </td>
              <td v-else-if="header.type == 'text'"
                :key="headerIndex"
                :class="{ 'has-text-grey-light': !user[header.field] }">
                {{ columnValue(user, header) }}
              </td>
              <td 
                v-else
                :key="headerIndex">
                <div class="buttons buttons-right">
                  <v-tooltip 
                    key="deactivate-user"
                    v-if="user.is_active" 
                    :message="'Deactivate user'">
                    <button
                      class="button is-small"
                      @click="updateActiveStatus(user, false)">
                      <span class="icon">
                        <i class="fas fa-minus-circle has-text-grey"></i>
                      </span>
                    </button>
                  </v-tooltip>
                  <v-tooltip
                    key="activate-user"
                    v-else 
                    :message="'Reactivate user'">
                    <button
                      class="button is-small"
                      @click="updateActiveStatus(user, true)">
                      <span class="icon">
                        <i class="fas fa-plus-circle has-text-success"></i>
                      </span>
                    </button>
                  </v-tooltip>
                </div>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </v-fade-transition>

    <add-user-modal 
      v-model="addingUser"
      :roles="roles"
      @finishCreate="afterAddUser"/>

    <v-confirm-modal 
      v-model="confirmModal.state"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :type="confirmModal.type"
      :confirm-text="confirmModal.confirmText"
      :show-cancel="confirmModal.showCancel"
      @confirm="confirmModal.confirmedCb ? confirmModal.confirmedCb() : null"
      @cancel="confirmModal.state = false"/>

    <bulk-update-users-modal
      v-model="bulkUpdating"/>
<!-- 
    <v-confirm-modal 
      v-model="confirmPolicy.state"
      :title="confirmPolicy.title"
      :message="confirmPolicy.message"
      :type="confirmPolicy.type"
      @confirm="confirmPolicy.policyConfirmed ? confirmPolicy.policyConfirmed() : null"
      @cancel="confirmPolicy.cancelPolicyReplication ? confirmPolicy.cancelPolicyReplication() : null"/>   

    <v-confirm-modal 
      v-model="confirmPassword.state"
      :title="confirmPassword.title"
      :message="confirmPassword.message"
      :type="confirmPassword.type"
      confirmText="Ok"
      :showCancel="false"
      @confirm="confirmPassword.passwordConfirmed ? confirmPassword.passwordConfirmed() : null"
      @cancel="confirmPassword.state = false"/>    -->

  </div>

</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import VProgress from '@/components/base/VProgress'
import VAlert from '@/components/base/VAlert'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import VSelect from '@/components/base/VSelect'
import VCheckbox from '@/components/base/VCheckbox'
import VSearch from '@/components/base/VSearch'
import VTooltip from '@/components/base/VTooltip'
import AddUserModal from '@/components/user/AddUserModal'
import BulkUpdateUsersModal from '@/components/user/BulkUpdateUsersModal'
import VConfirmModal from '@/components/base/VConfirmModal'

export default {
  components: {
    VProgress,
    VAlert,
    VFadeTransition,
    VSelect,
    VCheckbox,
    VSearch,
    VTooltip,
    AddUserModal,
    VConfirmModal,
    BulkUpdateUsersModal
  },
  data() {
    return {
      headers() {
        let headers = [
          { label: 'Id', field: 'id', show: false, type: 'text', sortable: true },
          { label: 'Name', field: 'display_name', show: true, type: 'text', sortable: true },
          { label: 'Email', field: 'email', show: true, type: 'text', sortable: true },
          { label: 'Phone Number', field: 'primary_phone', show: true, type: 'text', sortable: false },
          { label: 'Status', field: 'is_active', show: true, type: 'text', sortable: true },
          { label: 'Role', field: 'role', show: true, type: 'text', sortable: true },
          { label: 'Policy', field: 'policy', subField: 'id', show: true, type: 'select', sortable: false },
          { label: 'Sign In Method', field: 'sign_in_method', subField: null, show: true, type: 'text', sortable: true },
          { label: '', show: true }
        ];

        return headers.filter(h => h.show);
      },
      addingUser: false,
      confirmModal: {
        state: false,
        title: 'Inactivate Confirmation',
        message: 'Are you sure you want to inactivate the user <userName>?',
        type: 'danger',
        confirmText: 'Yes',
        showCancel: true,
        confirmedCb: null
      },
      bulkUpdating: false
    }
  },
  computed: {
    ...mapState("users", [
      "loading", "users", "filters", "orderBy", "statusOptions", "selected",
      "allSelected"
    ]),
    ...mapState("permissions", ["policies"]),
    ...mapGetters("users", ["filteredUserList", "roles"])
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("users", [
      "getUsers",
      "updateUser",
      "setFilter",
      "toggleAllSelected"
    ]),
    ...mapActions("permissions", ["getPolicies", "setUserPolicy"]),
    ...mapMutations("users", ["setFilter", "setOrderBy", "toggleSelected"]),
    initialize() {
      this.getUsers();
      this.getPolicies();
    },
    filterUsers(field, value) {
      this.clearSelection();
      this.setFilter({ field, value });
    },
    columnValue(user, header) {
      let column = 'N/A';
      let {field, subField} = header;

      column = (user[field] && user[field][subField]) ?  user[field][subField] : (user[field] ? user[field] : 'N/A');

      if (header.field === 'is_active') {
        column = user[field] ? 'Active' : 'Inactive'
      }
      
      if (['role', 'sign_in_method'].includes(header.field)) {
        column = this.$options.filters.titlecase(column.replaceAll('_', ' '))
      }

      return column;
    },
    selectUserPolicy(user, policyId) {
      const setPolicy = (user, policyId) => {
        if (user.policy) user.policy.id = policyId;
        else user.policy = { id: policyId };
      };

      setPolicy(user, policyId);
      this.saveUserPolicy(policyId, [user.id]);
    },
    saveUserPolicy(policyId, users) {
      let userData = { user_ids: users };

      this.setUserPolicy({ policyId, data: userData }).then(resp => {
        this.$emit('success', resp.data);
      });
    },
    updateActiveStatus(user, status) {
      const { confirmModal } = this;

      confirmModal.confirmedCb = () => {
        this.updateUser({ 
          id: user.id, 
          data: { is_active: status } 
        }).then(resp => {
          this.$emit('success', resp.data);
          confirmModal.state = false;

          this.initialize();
        });
      }

      let activateDeactivate = status ? 'reactivate' : 'deactivate';

      confirmModal.message = `Are you sure you want to ${activateDeactivate} user ${user.display_name}?`;
      confirmModal.confirmText = 'Yes';
      confirmModal.showCancel = true;
      confirmModal.state = true;
    },
    selectValue(user, header, isSubField) {
      let value = null;
      let {field, subField} = header;

      if (isSubField) value = user[field] && user[field][subField] ? user[field][subField] : null;
      else value = user[field] ? user[field] : null

      return value;
    },
    afterAddUser(data) {
      if (data) {
        this.showRandomPassword(data);
      }
    },
    showRandomPassword(createdUser) {
      let { display_name, random_password, email } = createdUser;
      const { confirmModal } = this;

      confirmModal.confirmedCb = () => {
        confirmModal.state = false;

        this.filters.is_active = true;
        this.initialize();
      }

      if (random_password) {
        confirmModal.message = `The default single-use password for the user ${display_name} (${email}) is:\n ${random_password}`;
        confirmModal.confirmText="Ok"
        confirmModal.showCancel="false"
        confirmModal.state = true;
      }
    },
    userIsSelected(userId) {
      return this.selected.includes(userId);
    },
    clearSelection() {
      this.setOrderBy({ field: null, direction: null });
      this.toggleAllSelected(false);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.order-by-button {
  color: lightgrey;
}

.rotatable {
  transition: transform 300ms ease-in-out;
  transform: rotate(0deg);
  &.desc {
    transform: rotate(180deg);
  }
}

th.is-sortable {
  cursor: pointer;
  
  &:hover .order-by-button {
    color:#007aff;
  }
}

.icon.is-clickable {
  cursor: pointer;
  &:hover {
    color:#007aff;
  }
}
</style>